// src/pages/BulkMetrics.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

interface ResultItem {
  domain: string;
  da: number;
  pa: number;
  spam_score: number;
  status: string;
}

export default function BulkMetrics() {
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [rawText, setRawText] = useState<string>("");
  const [domains, setDomains] = useState<string[]>([]);
  const [invalidLines, setInvalidLines] = useState<string[]>([]);
  const [tooMany, setTooMany] = useState<boolean>(false);
  const [warning, setWarning] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState<ResultItem[]>([]);

  // Simple regex for “something.tld” or “sub.somedomain.co.uk”
  const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

  // 1) On mount, ensure user is logged in and fetch credits
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    refreshUser();
  }, [token, navigate, refreshUser]);

  // 2) Parse domains & validate whenever rawText changes
  useEffect(() => {
    // Split by newline, trim, and filter out empty lines
    const lines = rawText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line !== "");

    const invalid: string[] = [];
    const valid: string[] = [];

    for (const rawLine of lines) {
      // 1) strip protocol
      let domain = rawLine.replace(/^https?:\/\//i, "");
      // 2) strip www.
      domain = domain.replace(/^www\./i, "");
      // 3) strip everything after first slash, question-mark or hash
      domain = domain.split(/[\/?#]/)[0].toLowerCase();

      if (!domainRegex.test(domain)) {
        invalid.push(rawLine);     // show original line in error
      } else {
        valid.push(domain);        // collect cleaned domain
      }
    }

    // Enforce max-50 limit
    setTooMany(valid.length > 50);

    setDomains(valid);
    setInvalidLines(invalid);
  }, [rawText]);

  // 3) Show a warning if domains length exceeds credits
  useEffect(() => {
    if (user && domains.length > user.credits) {
      setWarning(`You need ${domains.length} credits but only have ${user.credits}.`);
    } else {
      setWarning("");
    }
  }, [domains, user]);

  // 4) Fire the bulk-metrics request
  const handleRunCheck = async () => {
    if (
      invalidLines.length > 0 ||
      domains.length === 0 ||
      tooMany ||
      (user ? domains.length > user.credits : true)
    ) {
      return;
    }

    setLoading(true);
    setResults([]);
    setWarning("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/bulk-metrics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ domains }),
        }
      );

      if (response.status === 402) {
        const err = await response.json();
        setWarning(err.detail || "Not enough credits");
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        console.error("Bulk metrics error:", text);
        setWarning("An unexpected error occurred. Please try again.");
        setLoading(false);
        return;
      }

      const data: ResultItem[] = await response.json();
      setResults(data);

      // Refresh user’s credit count from server
      refreshUser();
    } catch (e) {
      console.error(e);
      setWarning("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 5) Copy table to clipboard
  const handleCopy = () => {
    if (!results.length) return;
    let text = "domain\tda\tpa\tspam_score\tstatus\n";
    results.forEach((r) => {
      text += `${r.domain}\t${r.da}\t${r.pa}\t${r.spam_score}\t${r.status}\n`;
    });
    navigator.clipboard.writeText(text);
    alert("Table copied to clipboard");
  };

  // 6) Download CSV
  const handleDownload = () => {
    if (!results.length) return;
    let csv = "domain,da,pa,spam_score,status\n";
    results.forEach((r) => {
      const d = r.domain.replace(/,/g, "");
      const s = r.status.replace(/,/g, "");
      csv += `${d},${r.da},${r.pa},${r.spam_score},${s}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_metrics.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-extrabold mb-6">Bulk DA/PA/Spam Score Checker</h1>

      {user && (
        <p className="mb-4">
          You have <strong>{user.credits}</strong> credits. Each domain costs 1 credit.
        </p>
      )}

      {/* Validation errors */}
      {invalidLines.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="font-semibold text-red-700">Please fix the following lines:</p>
          <ul className="list-disc list-inside text-red-600">
            {invalidLines.map((line) => (
              <li key={line}>
                <code>{line}</code> is not a valid domain or URL.
              </li>
            ))}
          </ul>
          <p className="text-sm text-red-700 mt-1">
            • Paste one domain or full URL per line.<br />
            • Valid examples: <code>example.com</code>, <code>https://sub.domain.co.uk/path</code>.
          </p>
        </div>
      )}

      {/* Too many warning */}
      {tooMany && (
        <p className="text-red-600 mb-4">
          You can only check up to 50 domains at a time. You’ve entered {domains.length}.
        </p>
      )}

      {/* Input textarea */}
      <textarea
        className="w-full h-40 border rounded p-2 mb-4 resize-none"
        placeholder="Enter one domain or URL per line..."
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
      />

      {/* Domain count & credit warning */}
      {domains.length > 0 && (
        <p className="mb-4">
          ↳ You’ve entered <strong>{domains.length}</strong> valid domain
          {domains.length !== 1 ? "s" : ""}.
          {user && domains.length > user.credits && (
            <span className="text-red-600 ml-2">
              (Need {domains.length}, only {user.credits} available)
            </span>
          )}
        </p>
      )}

      {/* Other warnings */}
      {warning && <p className="text-red-600 mb-4">{warning}</p>}

      {/* Run Check button */}
      <button
        className={`bg-blue-600 text-white px-4 py-2 rounded ${
          loading ||
          domains.length === 0 ||
          invalidLines.length > 0 ||
          (user ? domains.length > user.credits : true) ||
          tooMany
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-blue-700"
        }`}
        onClick={handleRunCheck}
        disabled={
          loading ||
          domains.length === 0 ||
          invalidLines.length > 0 ||
          (user ? domains.length > user.credits : true) ||
          tooMany
        }
      >
        {loading ? "Running..." : "Run Check"}
      </button>

      {/* Results table */}
      {results.length > 0 && (
        <div className="mt-8">
          <div className="flex space-x-4 mb-4">
            <button
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
              onClick={handleCopy}
            >
              Copy Table
            </button>
            <button
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
              onClick={handleDownload}
            >
              Download CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Domain</th>
                  <th className="px-4 py-2 border">DA</th>
                  <th className="px-4 py-2 border">PA</th>
                  <th className="px-4 py-2 border">Spam Score</th>
                  <th className="px-4 py-2 border">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr
                    key={r.domain}
                    className={r.status.startsWith("error") ? "bg-red-50" : ""}
                  >
                    <td className="px-4 py-2 border">{r.domain}</td>
                    <td className="px-4 py-2 border">{r.da}</td>
                    <td className="px-4 py-2 border">{r.pa}</td>
                    <td className="px-4 py-2 border">{r.spam_score}</td>
                    <td className="px-4 py-2 border">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
